/**
 * Storage versioning and migration system.
 *
 * @module storageVersion
 *
 * Manages storage schema versions and provides migration paths when data structures change.
 * Prevents data loss and ensures backward compatibility across application updates.
 *
 * @example
 * ```typescript
 * // In your data loading function:
 * const data = migrateStorage(STORAGE_KEYS.NUTRITION_DATA, [])
 * ```
 */

import { STORAGE_KEYS, type StorageKey } from '@/config/storageKeys'
import { logger } from '@/services/logger'

/**
 * Current storage schema version.
 * Increment this when making breaking changes to any stored data structure.
 */
export const CURRENT_STORAGE_VERSION = 1

/**
 * Migration function signature.
 * Takes data in old format and returns data in new format.
 */
export type MigrationFunction = (oldData: unknown) => unknown

/**
 * Migration registry mapping version transitions to migration functions.
 * Key format: "fromVersion-toVersion"
 *
 * @example
 * ```typescript
 * migrations['0-1'] = (data) => {
 *   // Migrate from v0 to v1
 *   return transformedData
 * }
 * ```
 */
export const migrations: Record<string, MigrationFunction> = {
  /**
   * Migration from v0 (unversioned) to v1.
   * This is the baseline migration for existing users.
   */
  '0-1': (data: unknown): unknown => {
    // V0 to V1: No schema changes, just add version tracking
    logger.info('Migrating storage from v0 to v1 (baseline)')
    return data
  },

  // Future migrations will be added here
  // '1-2': (data: unknown): unknown => {
  //   // Example: Add new required field
  //   if (Array.isArray(data)) {
  //     return data.map(entry => ({
  //       ...entry,
  //       newField: 'defaultValue'
  //     }))
  //   }
  //   return data
  // },
}

/**
 * Get the stored version number for a specific storage key.
 * Returns 0 if no version is tracked (unversioned legacy data).
 *
 * @param storageKey - The storage key to check version for
 * @returns The version number, or 0 for unversioned data
 */
export function getStoredVersion(storageKey: StorageKey): number {
  try {
    const versionKey = `${storageKey}_version`
    const versionStr = localStorage.getItem(versionKey)
    if (versionStr === null) return 0

    const version = parseInt(versionStr, 10)
    return isNaN(version) ? 0 : version
  } catch (error) {
    logger.error(`Failed to get stored version for ${storageKey}`, error)
    return 0
  }
}

/**
 * Set the version number for a specific storage key.
 *
 * @param storageKey - The storage key to set version for
 * @param version - The version number to store
 */
export function setStoredVersion(storageKey: StorageKey, version: number): void {
  try {
    const versionKey = `${storageKey}_version`
    localStorage.setItem(versionKey, version.toString())
  } catch (error) {
    logger.error(`Failed to set version for ${storageKey}`, error)
  }
}

/**
 * Apply a single migration step.
 *
 * @param data - The data to migrate
 * @param fromVersion - Starting version
 * @param toVersion - Target version
 * @returns Migrated data
 * @throws Error if migration function is not found
 */
function applyMigration(data: unknown, fromVersion: number, toVersion: number): unknown {
  const migrationKey = `${fromVersion}-${toVersion}`
  const migrationFn = migrations[migrationKey]

  if (migrationFn === undefined) {
    throw new Error(`No migration path from version ${fromVersion} to ${toVersion}`)
  }

  logger.info(`Applying migration ${migrationKey}`)
  return migrationFn(data)
}

/**
 * Migrate data through all necessary version steps.
 *
 * @param data - The data to migrate
 * @param fromVersion - Current version of the data
 * @param toVersion - Target version to migrate to
 * @returns Migrated data
 */
function migrateData(data: unknown, fromVersion: number, toVersion: number): unknown {
  let currentData = data
  let currentVersion = fromVersion

  // Apply migrations sequentially from current version to target version
  while (currentVersion < toVersion) {
    const nextVersion = currentVersion + 1
    currentData = applyMigration(currentData, currentVersion, nextVersion)
    currentVersion = nextVersion
  }

  return currentData
}

/**
 * Load and migrate storage data if needed.
 * Automatically handles version detection and migration chain execution.
 *
 * @template T - The expected type of the stored data
 * @param storageKey - The storage key to load from
 * @param defaultValue - Default value if no data exists or migration fails
 * @returns The loaded and migrated data, or defaultValue on error
 *
 * @example
 * ```typescript
 * // Load nutrition data with automatic migration
 * const nutritionData = migrateStorage<DailyNutrition[]>(
 *   STORAGE_KEYS.NUTRITION_DATA,
 *   []
 * )
 * ```
 */
export function migrateStorage<T>(storageKey: StorageKey, defaultValue: T): T {
  try {
    // Get stored data
    const rawData = localStorage.getItem(storageKey)
    if (rawData === null || rawData === '') {
      // No data exists, set current version and return default
      setStoredVersion(storageKey, CURRENT_STORAGE_VERSION)
      return defaultValue
    }

    // Parse stored data
    const parsedData: unknown = JSON.parse(rawData)

    // Check current version
    const storedVersion = getStoredVersion(storageKey)

    if (storedVersion === CURRENT_STORAGE_VERSION) {
      // Data is already at current version, no migration needed
      return parsedData as T
    }

    if (storedVersion > CURRENT_STORAGE_VERSION) {
      // Data is from a newer version (user downgraded?)
      logger.warn(
        `Storage data for ${storageKey} is from newer version ${storedVersion}, current is ${CURRENT_STORAGE_VERSION}. Using as-is.`
      )
      return parsedData as T
    }

    // Data needs migration
    logger.info(
      `Migrating ${storageKey} from version ${storedVersion} to ${CURRENT_STORAGE_VERSION}`
    )

    const migratedData = migrateData(parsedData, storedVersion, CURRENT_STORAGE_VERSION)

    // Save migrated data and update version
    localStorage.setItem(storageKey, JSON.stringify(migratedData))
    setStoredVersion(storageKey, CURRENT_STORAGE_VERSION)

    logger.info(`Successfully migrated ${storageKey} to version ${CURRENT_STORAGE_VERSION}`)

    return migratedData as T
  } catch (error) {
    logger.error(`Failed to migrate storage for ${storageKey}`, error)
    return defaultValue
  }
}

/**
 * Reset storage version for a specific key.
 * Useful for testing or forcing re-migration.
 *
 * @param storageKey - The storage key to reset version for
 */
export function resetStorageVersion(storageKey: StorageKey): void {
  try {
    const versionKey = `${storageKey}_version`
    localStorage.removeItem(versionKey)
    logger.info(`Reset storage version for ${storageKey}`)
  } catch (error) {
    logger.error(`Failed to reset version for ${storageKey}`, error)
  }
}

/**
 * Get storage health information for all tracked keys.
 * Useful for debugging and user support.
 *
 * @returns Object mapping storage keys to their versions and migration status
 */
export function getStorageHealth(): Record<
  string,
  { version: number; needsMigration: boolean; hasData: boolean }
> {
  const health: Record<string, { version: number; needsMigration: boolean; hasData: boolean }> = {}

  Object.values(STORAGE_KEYS).forEach((key) => {
    const version = getStoredVersion(key)
    const hasData = localStorage.getItem(key) !== null
    const needsMigration = version < CURRENT_STORAGE_VERSION && hasData

    health[key] = {
      version,
      needsMigration,
      hasData,
    }
  })

  return health
}
